import React from 'react';
import {
  Container,
  CssBaseline,
  Box,
  Typography,
  TextField,
  Button,
  CircularProgress,
} from '@mui/material';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useSnackbar } from 'notistack';
import agent from '../api/axios';

export default function ResetPassword() {
  const { enqueueSnackbar } = useSnackbar();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token') ?? '';
  const navigate = useNavigate();

  const [password, setPassword] = React.useState('');
  const [confirm, setConfirm] = React.useState('');
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);

  React.useEffect(() => {
    if (!token)
      setError('No reset token provided. Use the link from your email.');
  }, [token]);

  // password rules: min 8, 1 uppercase, 1 lowercase, 1 number, 1 special char
  const passwordRegex =
    /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[^A-Za-z0-9]).{8,}$/;
  const passwordValid = passwordRegex.test(password);
  const passwordsMatch = password === confirm;

  const valid = passwordValid && passwordsMatch;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    if (!token) return setError('Missing token.');
    if (!passwordValid)
      return setError(
        'Password must be at least 8 characters and include uppercase, lowercase, number and special character.'
      );
    if (!passwordsMatch) return setError('Passwords do not match.');

    setLoading(true);
    try {
      const resp = await agent.Auth.resetPassword(token, password);
      const data = resp?.data ?? resp;
      enqueueSnackbar(
        data?.message ?? 'Password reset successful. Please sign in.',
        { variant: 'success' }
      );
      navigate('/login', { replace: true });
    } catch (err: any) {
      console.error(err);
      const msg =
        err?.response?.data?.message ||
        err?.message ||
        'Failed to reset password.';
      setError(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Container component="main" maxWidth="xs">
      <CssBaseline />
      <Box
        sx={{
          marginTop: 8,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
        }}
      >
        <Typography component="h1" variant="h5" gutterBottom>
          Reset Password
        </Typography>

        <Box
          component="form"
          onSubmit={handleSubmit}
          sx={{ mt: 1, width: '100%' }}
        >
          <TextField
            label="New password"
            type="password"
            required
            fullWidth
            margin="normal"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            helperText={
              password
                ? passwordValid
                  ? 'Strong password'
                  : 'Must be 8+ chars, include uppercase, lowercase, number and special character'
                : 'At least 8 characters, include uppercase, lowercase, number and special char'
            }
            error={!!password && !passwordValid}
            disabled={loading}
          />
          <TextField
            label="Confirm password"
            type="password"
            required
            fullWidth
            margin="normal"
            value={confirm}
            onChange={(e) => setConfirm(e.target.value)}
            error={!!confirm && !passwordsMatch}
            helperText={
              confirm && !passwordsMatch ? 'Passwords do not match' : ''
            }
            disabled={loading}
          />

          {error && (
            <Typography color="error" variant="body2" sx={{ mt: 1 }}>
              {error}
            </Typography>
          )}

          <Box sx={{ position: 'relative', mt: 3 }}>
            <Button
              type="submit"
              fullWidth
              variant="contained"
              disabled={!valid || loading || !!error}
            >
              Reset password
            </Button>
            {loading && (
              <CircularProgress
                size={24}
                sx={{
                  position: 'absolute',
                  top: '50%',
                  left: '50%',
                  marginTop: '-12px',
                  marginLeft: '-12px',
                }}
              />
            )}
          </Box>
        </Box>
      </Box>
    </Container>
  );
}
